import React, { CSSProperties } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  type Column
} from '@tanstack/react-table'
import { classNames } from 'src/lib/utils/helpers'
import { GenericTableProps, ScrollMode, GenericTableColumnMeta } from './table-types'
import { useInfiniteScroll } from './hooks/useInfiniteScroll'
import { TablePagination } from './pagination/TablePagination'
import { useLocale } from 'src/lib/hooks/useLocal'
import { Icon } from '@shopflo/ui'
import { Tray } from '@phosphor-icons/react'
import { getHorizontalDividerClass, getCellBorderClasses } from './table-style-helpers'

const overlayAnchorClass =
  'w-0 min-w-0 max-w-0 p-0 border-0 align-middle overflow-visible relative bg-transparent'

const overlayCheckboxWrapClass =
  'pointer-events-auto absolute right-full top-1/2 z-10 flex -translate-y-1/2 items-center w-fit'

export function GenericTableV2<T extends object>({
  data,
  columns,
  scrollMode = ScrollMode.PAGINATION,
  pagination,
  infiniteScroll,
  sorting,
  filtering,
  rowSelection,
  columnVisibility,
  tableOptions = {},
  isLoading = false,
  onRowClick,
  getRowId,
  customizationConfig,
  overlayRowSelection,
  selectedRowId,
  highlightedRowId,
  stickyHeaderTop,
  scrollInParent = false,
  cellBorders = 'none',
  headerBorders = 'horizontal',
  roundedRows = true,
  tableStyle,
  tableLayout = 'auto',
  getColumnStyle,
  backgroundTransparent = false,
  tableLayoutAuto = false,
  pageHeader
}: GenericTableProps<T>) {
  const { t } = useLocale()

  const hasOverlay = Boolean(overlayRowSelection)
  const showRoundedRows = hasOverlay || roundedRows
  const hasCellDividers = cellBorders !== 'none'
  const stickyHeader = stickyHeaderTop != null
  const fullColCount = columns.length + (hasOverlay ? 1 : 0)
  const bgClass = backgroundTransparent ? 'bg-transparent' : 'bg-zinc-50'
  const headerBgClass = 'bg-zinc-50'

  const getDefaultColumnStyle = (column: Column<T, unknown>): CSSProperties => {
    const width = column.getSize()
    const { minSize, maxSize, size } = column.columnDef

    if (minSize != null && maxSize == null && size == null) {
      return { minWidth: minSize }
    }

    return {
      width,
      minWidth: minSize ?? 0,
      maxWidth: maxSize ?? size ?? width
    }
  }

  const resolveColumnStyle = (columnId: string, column: Column<T, unknown>) => {
    const custom = getColumnStyle?.(columnId, column)
    if (custom != null) return custom
    return getDefaultColumnStyle(column)
  }

  const table = useReactTable({
    data: data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),

    getPaginationRowModel: scrollMode === ScrollMode.PAGINATION ? getPaginationRowModel() : undefined,
    manualPagination: scrollMode === ScrollMode.PAGINATION,
    pageCount: pagination
      ? (pagination.pageCount === -1 ? 10000 : pagination.pageCount) ??
        Math.ceil((pagination.totalRows ?? data.length) / pagination.pageSize)
      : 1,

    getSortedRowModel: sorting?.manualSorting ? undefined : getSortedRowModel(),
    manualSorting: sorting?.manualSorting,

    getFilteredRowModel: filtering?.manualFiltering ? undefined : getFilteredRowModel(),
    manualFiltering: filtering?.manualFiltering,

    enableColumnResizing: false,
    columnResizeMode: 'onChange',

    state: {
      pagination: pagination
        ? {
            pageIndex: pagination.pageIndex,
            pageSize: pagination.pageSize
          }
        : undefined,
      sorting: sorting?.state,
      columnFilters: filtering?.state,
      rowSelection: rowSelection?.state,
      columnVisibility: columnVisibility?.state
    },

    onPaginationChange: pagination?.onPaginationChange,
    onSortingChange: sorting?.onSortingChange,
    onColumnFiltersChange: filtering?.onFilteringChange,
    onRowSelectionChange: rowSelection?.onRowSelectionChange,
    onColumnVisibilityChange: columnVisibility?.onVisibilityChange,

    getRowId: getRowId,
    enableMultiRowSelection: rowSelection?.enableMultiRowSelection,

    ...tableOptions
  })

  const { sentryRef } = useInfiniteScroll<HTMLDivElement>({
    enabled: scrollMode === ScrollMode.INFINITE,
    onLoadMore: infiniteScroll?.onLoadMore || (() => {}),
    hasMore: infiniteScroll?.hasMore || false,
    isLoading: infiniteScroll?.isLoading || false,
    threshold: infiniteScroll?.threshold || 200
  })

  const renderLoadingState = () => {
    return Array.from({ length: 6 }).map((_, index) => (
      <tr key={index} className={`${bgClass} `}>
        {hasOverlay ? (
          <td className={overlayAnchorClass}>
            <span className='sr-only' aria-hidden />
          </td>
        ) : null}
        {columns.map((column, colIndex) => (
          <td
            key={String(column.id)}
            className={classNames(
              'py-4 px-2',
              hasCellDividers ? 'overflow-visible' : 'overflow-hidden text-overflow-ellipsis',
              ...getCellBorderClasses({
                cellBorders,
                isFirstColumn: colIndex === 0,
                isLastColumn: colIndex === columns.length - 1
              })
            )}
          >
            <div className='h-3 w-full rounded bg-zinc-100 animate-pulse' />
          </td>
        ))}
      </tr>
    ))
  }

  const renderTableBody = () => {
    const shouldShowLoading = isLoading

    if (shouldShowLoading) {
      return renderLoadingState()
    }

    if ((data?.length ?? 0) === 0) {
      return (
        <tr>
          <td colSpan={fullColCount} className={`${bgClass} text-center font-sm text-gray-light p-4 h-96`}>
            <div className='inline-flex flex-col items-center justify-center'>
              <Icon IconComponent={Tray} className='text-zinc-500' iconProps={{ weight: 'duotone' }} size='xl' />
              <span className='text-text-sm-medium text-zinc-500'>{t('no_data_found')}</span>
            </div>
          </td>
        </tr>
      )
    }

    return (
      <>
        {table.getRowModel().rows.map(row => (
          <tr
            key={row.id}
            data-row-id={row.id}
            className={classNames(
              'group',
              hasOverlay ? 'w-full' : 'min-w-full',
              onRowClick ? 'cursor-pointer' : '',
              selectedRowId !== undefined && row.id === selectedRowId ? 'is-selected' : ''
            )}
            onClick={e => {
              e.stopPropagation()
              onRowClick && onRowClick(row.original)
            }}
          >
            {hasOverlay && overlayRowSelection ? (
              <td className={classNames(overlayAnchorClass, 'group-hover:bg-zinc-500/5')}>
                <div className={overlayCheckboxWrapClass} onClick={e => e.stopPropagation()}>
                  {overlayRowSelection.renderOverlayCell(row)}
                </div>
              </td>
            ) : null}
            {row.getVisibleCells().map((cell, cellIndex) => {
              const cellMeta = cell.column.columnDef.meta as GenericTableColumnMeta | undefined
              const visibleCount = row.getVisibleCells().length
              const isFirstData = cellIndex === 0
              const isLastData = cellIndex === visibleCount - 1
              const isSelected = selectedRowId !== undefined && row.id === selectedRowId
              const isHighlighted = highlightedRowId !== undefined && row.id === highlightedRowId

              return (
                <td
                  key={cell.id}
                  className={classNames(
                    'text-coal-light text-sm',
                    hasCellDividers ? 'overflow-visible' : 'overflow-hidden text-overflow-ellipsis',
                    'group-hover:bg-zinc-500/5',
                    isSelected ? 'bg-zinc-100' : '',
                    !isSelected && isHighlighted ? 'bg-amber-50 transition-[background-color] duration-[600ms] ease' : '',
                    ...getCellBorderClasses({
                      cellBorders,
                      isFirstColumn: isFirstData,
                      isLastColumn: isLastData
                    }),
                    showRoundedRows && isFirstData ? 'rounded-l-xl' : '',
                    showRoundedRows && isLastData ? 'rounded-r-xl' : '',
                    cellMeta?.bodyCellClassName ?? ''
                  )}
                  style={resolveColumnStyle(cell.column.id, cell.column)}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              )
            })}
          </tr>
        ))}

        {scrollMode === ScrollMode.INFINITE && (
          <tr className='h-0 invisible'>
            <td colSpan={fullColCount} className='p-0 border-0'>
              <div ref={sentryRef} className='h-px'></div>
            </td>
          </tr>
        )}
        {scrollMode === ScrollMode.INFINITE && infiniteScroll?.isLoading && renderLoadingState()}
      </>
    )
  }

  return (
    <div
      className={classNames(
        'flex w-full min-w-0 flex-col rounded-lg',
        scrollInParent ? '' : 'max-w-full overflow-hidden'
      )}
    >
      <div className='flex w-full min-w-0 flex-col gap-1'>
        {pageHeader ? (
          <div className='sticky top-0 z-20 bg-zinc-50/50 backdrop-blur-[10px]'>{pageHeader}</div>
        ) : null}
        <div
          className={classNames(
            `w-full min-w-0 rounded-lg ${bgClass}`,
            scrollInParent ? '' : 'overflow-y-auto overflow-x-auto scrollbar-hide'
          )}
        >
          <table
            className={classNames(
              'w-full border-separate border-spacing-0',
              tableLayoutAuto
                ? 'table-auto'
                : tableLayout === 'fixed'
                  ? 'table-fixed'
                  : hasOverlay
                    ? 'table-auto'
                    : 'table-auto min-w-max'
            )}
            style={tableStyle}
          >
            {!customizationConfig?.header?.hideHeader && (
              <thead className={classNames(headerBgClass, hasOverlay ? 'group' : '')}>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {hasOverlay && overlayRowSelection ? (
                      <th
                        className={classNames(overlayAnchorClass, stickyHeader ? `sticky z-[20] ${headerBgClass}` : '')}
                        style={
                          stickyHeader
                            ? { top: stickyHeaderTop, boxShadow: '-48px 0 0 0 #FAFAFA' }
                            : undefined
                        }
                      >
                        <div className={overlayCheckboxWrapClass} onClick={e => e.stopPropagation()}>
                          {overlayRowSelection.renderOverlayHeader(table)}
                        </div>
                      </th>
                    ) : null}
                    {headerGroup.headers.map((header, hIdx) => {
                      const isLastHeader = hIdx === headerGroup.headers.length - 1
                      const headerMeta = header.column.columnDef.meta as GenericTableColumnMeta | undefined

                      return (
                        <th
                          key={header.id}
                          data-column-id={header.column.id}
                          colSpan={header.colSpan}
                          className={classNames(
                            `relative py-2 px-2 items-center text-overflow-ellipsis ${headerBgClass} text-left text-text-xs-medium text-zinc-500`,
                            showRoundedRows && hIdx === 0 ? 'rounded-tl-xl' : '',
                            showRoundedRows && isLastHeader ? 'rounded-tr-xl' : '',
                            headerBorders === 'horizontal'
                              ? getHorizontalDividerClass({
                                  isFirstColumn: hIdx === 0,
                                  isLastColumn: isLastHeader,
                                  colorClass: 'after:bg-zinc-200'
                                })
                              : '',
                            headerMeta?.headerCellClassName ?? ''
                          )}
                          style={{
                            ...resolveColumnStyle(header.column.id, header.column),
                            ...(stickyHeader
                              ? { position: 'sticky', top: stickyHeaderTop, zIndex: 19 }
                              : {})
                          }}
                        >
                          <div className='flex items-center text-text-xs-medium text-zinc-500'>
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                          </div>
                        </th>
                      )
                    })}
                  </tr>
                ))}
              </thead>
            )}

            <tbody className='border-b-1'>
              <tr aria-hidden>
                <td colSpan={fullColCount} className='h-1 p-0 border-0' />
              </tr>
              {renderTableBody()}
              <tr aria-hidden>
                <td colSpan={fullColCount} className='h-1 p-0 border-0' />
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {scrollMode === ScrollMode.PAGINATION && pagination ? (
        <TablePagination table={table} pagination={pagination} backgroundTransparent={backgroundTransparent} />
      ) : null}
    </div>
  )
}

export default GenericTableV2
